import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { UiSelect } from 'components/common'
import { Button, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Textarea, VStack } from '@chakra-ui/react'
import { getInstallationVendors } from 'api'
import { useImmer } from 'use-immer'
import { useAppContext } from 'hooks/useAppContext'
import { format as formatDate } from 'date-fns'
import { createProjectInstallerPO, updateProjectInstallerPO } from 'api/projectInstallerPOApi'
import { getAssetUrl } from 'lib'

export const PROJECT_INSTALLER_PO_CATEGORIES = {
  Installation: 'Installation',
  Permit: 'Permit',
  Variance: 'Variance',
  Manual: 'Manual'
}

const initialForm = {
  name: '',
  company: '',
  contact: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  price: 0,
  eta: null,
  note: '- Obtain all necessary permits\n- Site Survey (Contact GRS when on site. Collect structural dimensions)\n- Receive and inspect all signage from GRS (If freight damage is discovered mark BOL and deny delivery)\n- Remove and dispose of existing sign\n- Dig footer, pour concrete, set pole (steel provided by GRS)\n- Mount and attach sign per shop drawing (attach signage via through-bolt or welding) Hardware not included\n- Connect sign to electric (adequate electrical & grounding rods to be provided by others)\n- Mount and connect Wifi antennas (data to be ran by others)\n- The sign is ethernet, so you DO NOT need to run any data cables or mount wifi antennas'
}

export const ModalProjectInstallerPOFormType = {
  Add: 'Add',
  Edit: 'Edit',
  Approve: 'Approve'
}

export const ModalProjectInstallerPOForm = ({ isOpen, onClose, type = ModalProjectInstallerPOFormType.Add, po, projectId, reload }) => {
  const { createToast } = useAppContext()
  const [form, setForm] = useImmer(initialForm)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState(null)

  useEffect(() => {
    const load = async () => {
      if (type === ModalProjectInstallerPOFormType.Add || !po) {
        setForm(initialForm)
        setSelectedVendor(null)
      } else {
        setForm(draft => {
          draft.name = po.name || ''
          draft.company = po.company || ''
          draft.contact = po.contact || ''
          draft.phone = po.phone || ''
          draft.email = po.email || ''
          draft.address = po.address || ''
          draft.city = po.city || ''
          draft.state = po.state || ''
          draft.zip = po.zip || ''
          draft.price = po.price || 0
          draft.eta = po.eta || null
          draft.note = po.note || ''
        })
        setSelectedVendor({
          name: po.company || '',
          contact: po.contact || '',
          phone: po.phone || '',
          email: po.email || '',
          address: po.address || '',
          city: po.city || '',
          state: po.state || '',
          zip: po.zip || ''
        })
      }
    }
    load()
  }, [type, po])

  const handleSave = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      let response
      if (type === ModalProjectInstallerPOFormType.Add) {
        response = await createProjectInstallerPO({ ...form, price: form.price * 1, projectId })
      } else {
        response = await updateProjectInstallerPO(po.id, { ...form, price: form.price * 1 })
      }
      window.open(getAssetUrl(response.pdf))
      createToast('success', 'Success', 'Po saved.')
      setIsSaving(false)
      onClose()
      reload()
      setSelectedVendor(null)
      setForm(initialForm)
    } catch (err) {
      setIsSaving(false)
      createToast('error', 'Error', err)
    }
  }

  const handleChange = (e) => {
    setForm(draft => { draft[e.target.name] = e.target.value })
  }

  const eta = useMemo(() => {
    const date = new Date(form.eta)
    return form.eta ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'yyyy-MM-dd') : ''
  }, [form.eta])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSave}>
          <ModalHeader>Installer PO</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack alignItems="none" gap={4}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Item Name</FormLabel>
                    <Select name="name" value={form.name} onChange={handleChange} placeholder="Select">
                      {Object.keys(PROJECT_INSTALLER_PO_CATEGORIES).map(v =>
                        <option key={PROJECT_INSTALLER_PO_CATEGORIES[v]} value={PROJECT_INSTALLER_PO_CATEGORIES[v]}>{PROJECT_INSTALLER_PO_CATEGORIES[v]}</option>
                      )}
                    </Select>
                  </FormControl>
                </GridItem>
              </Grid>
              <Heading size="sm">Vendor</Heading>
              <Grid templateColumns="repeat(2, 1fr)" columnGap={4} rowGap={2}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Company</FormLabel>
                    <UiSelect
                      value={selectedVendor}
                      onChange={v => {
                        setSelectedVendor(v)
                        setForm(draft => {
                          draft.company = v?.name || ''
                          draft.contact = v?.contact || ''
                          draft.phone = v?.phone || ''
                          draft.email = v?.name || ''
                          draft.address = v?.address || ''
                          draft.city = v?.city || ''
                          draft.state = v?.state || ''
                          draft.zip = v?.zip || ''
                        })
                      }}
                      loadData={(page, search) => {
                        return getInstallationVendors(page, 20, search, 'name', 'asc')
                      }}
                      apiRespProp="rows"
                      getOptionValue={v => v.id}
                      getOptionLabel={v => v.name}
                      placeholder="Search"
                      noOptionsMessage={() => 'Not Found'}
                      required={true}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Phone</FormLabel>
                    <Input type="text" name="phone" value={form.phone} onChange={handleChange} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Contact</FormLabel>
                    <Input type="text" name="contact" value={form.contact} onChange={handleChange} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input type="text" name="email" value={form.email} onChange={handleChange} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Address</FormLabel>
                    <Input type="text" name="address" value={form.address} onChange={handleChange} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>City</FormLabel>
                    <Input type="text" name="city" value={form.city} onChange={handleChange} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input type="text" name="state" value={form.state} onChange={handleChange} />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Zip</FormLabel>
                    <Input type="text" name="zip" value={form.zip} onChange={handleChange} />
                  </FormControl>
                </GridItem>
              </Grid>
              <Heading size="sm">Extra</Heading>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Price</FormLabel>
                    <Input
                      type="number"
                      value={form.price}
                      onChange={e => {
                        setForm(draft => {
                          draft.price = e.target.value
                        })
                      }}
                      onBlur={e => {
                        setForm(draft => {
                          draft.price = parseFloat(e.target.value || 0).toFixed(2)
                        })
                      }}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>ETA</FormLabel>
                    <Input type="date" name="eta" value={eta} onChange={handleChange} />
                  </FormControl>
                </GridItem>
              </Grid>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea name="note" value={form.note} onChange={handleChange} rows="10" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end" gap={4}>
              <Button type="submit" colorScheme="blue" isLoading={isSaving}>
                Save
              </Button>
              <Button onClick={onClose}>Close</Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

ModalProjectInstallerPOForm.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  type: PropTypes.oneOf(Object.values(ModalProjectInstallerPOFormType)),
  po: PropTypes.object,
  projectId: PropTypes.number,
  reload: PropTypes.func
}
